// /w/:wid/city/:vid/* — the city's screens share one village/get (polled), the city header with the switcher, the resource HUD
// and the action runner. `useCity()` gives every screen the same view of the city.
import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { Navigate, Outlet, useLocation, useNavigate, useParams } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import type { Village } from "@/api/types";
import { Page, type HeaderSpec, type PageLayout } from "@/app/Page";
import { cityMeta, menuBadgesOf, navBadgesOf } from "@/app/WorldPage";
import { CitySwipe } from "@/kit/CitySwipe";
import { Sheet } from "@/kit/overlays";
import { CityPickList } from "@/kit/CityPick";
import { Button, Card, Empty, Skeleton } from "@/kit/primitives";
import { useCityActions } from "@/state/actions";
import { keys, ownVillages, useVillage } from "@/state/queries";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { fmtNum } from "@/game/format";

export interface CityCtx {
  wid: number;
  vid: number;
  v: Village | undefined;
  loading: boolean;
  error: boolean;
  actions: ReturnType<typeof useCityActions>;
  /** the city's base path: /w/2/city/1001 */
  base: string;
  openSwitcher: () => void;
}

const Ctx = createContext<CityCtx | null>(null);

export const useCity = (): CityCtx => {
  const c = useContext(Ctx);
  if (!c) throw new Error("useCity outside a city route");
  return c;
};

export function CityRoute() {
  const { wid: widP, vid: vidP } = useParams();
  const wid = Number(widP);
  const vid = Number(vidP);
  const session = useSession();
  const setVillage = useSession((s) => s.setVillage);
  const nav = useNavigate();
  const qc = useQueryClient();
  const [switcher, setSwitcher] = useState(false);
  const q = useVillage(wid, vid);
  const v = q.data;
  const actions = useCityActions(wid, vid);

  useEffect(() => {
    if (Number.isInteger(vid) && vid > 0 && session.vid !== vid) setVillage(vid);
  }, [vid, session.vid, setVillage]);

  // the game answered with another city (this one isn't ours any more) or with nothing left in the world
  useEffect(() => {
    if (!v) return;
    if (v.on_app_init_state === "loadNextVillage" && v.village?.id && v.village.id !== vid) {
      qc.setQueryData(keys.village(wid, v.village.id), v);
      nav(`/w/${wid}/city/${v.village.id}`, { replace: true });
    }
    if (v.on_app_init_state === "rimmed") {
      usePrompts.getState().dialogShow("No cities left", "You have no city in this world any more. Start over from the Worlds screen.");
      nav("/worlds", { replace: true });
    }
  }, [v, vid, wid, qc, nav]);

  const ctx = useMemo<CityCtx>(
    () => ({ wid, vid, v, loading: q.isLoading, error: q.isError, actions, base: `/w/${wid}/city/${vid}`, openSwitcher: () => setSwitcher(true) }),
    [wid, vid, v, q.isLoading, q.isError, actions],
  );

  if (!Number.isInteger(vid) || vid <= 0) return <Navigate to={`/w/${wid}`} replace />;

  return (
    <Ctx.Provider value={ctx}>
      <Outlet />
      <CitySwitcher open={switcher} onClose={() => setSwitcher(false)} />
      {/* phones: swipe the header / HUD / nav to the next or previous city, staying on the same screen (and unit, tab…) */}
      <CitySwipe wid={wid} vid={vid} cities={ownVillages(v, session.uid)}
        onSwitch={(id) => nav(window.location.pathname.replace(/\/city\/\d+/, `/city/${id}`) + window.location.search, { replace: true })} />
    </Ctx.Provider>
  );
}

/** A city screen's frame: the world header (its strip carries the resources) and the nav. `hud` is kept for callers;
 *  the resource row now lives in the header on every world screen (Classic Frame). */
export function CityPage({ children, header, layout }: { children: ReactNode; hud?: boolean; header?: Partial<HeaderSpec>; layout?: PageLayout }) {
  const { v, loading, error, openSwitcher } = useCity();
  const session = useSession();
  const cities = ownVillages(v, session.uid);
  const spec: HeaderSpec = {
    kind: "city",
    city: v?.village?.city_name ?? (loading ? "…" : "City"),
    meta: cityMeta(v, cities.length),
    onCity: openSwitcher,
    v,
    ...(header as object),
  } as HeaderSpec;
  return (
    <Page
      header={spec}
      layout={layout}
      badges={navBadgesOf(v)}
      menuBadges={menuBadgesOf(v)}
      notifications={v?.new_notifications ? 1 : undefined}
      avatarLevel={v?.player_stats?.rank ? fmtNum(v.player_stats.rank) : undefined}
    >
      {!v && loading ? (
        <div className="stack"><Skeleton h={220} /><Skeleton h={320} /></div>
      ) : !v && error ? (
        <Card><Empty>Couldn't read this city. <Button size="sm" onClick={() => window.location.reload()}>Try again</Button> or <a className="link" href="/worlds">pick another world</a></Empty></Card>
      ) : (
        children
      )}
    </Page>
  );
}

function CitySwitcher({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { v, vid, wid } = useCity();
  const session = useSession();
  const nav = useNavigate();
  const cities = ownVillages(v, session.uid);
  const { pathname } = useLocation();
  // switching from the Rally Point stays in the Rally Point (same tab) of the new city; elsewhere it opens the city view
  const rally = pathname.match(/\/city\/\d+\/(rally(?:\/[a-z]+)?)/)?.[1];
  const switchTo = (id: number) => `/w/${wid}/city/${id}${rally ? `/${rally}` : ""}`;
  return (
    <Sheet open={open} onClose={onClose} from="top" anchor=".whdr-bar" label="Choose a city" title={<h3 className="eyebrow" style={{ margin: "0 0 14px" }}>Your cities · World {wid}</h3>}>
      <CityPickList wid={wid} cities={cities} current={vid} empty="No other cities." onPick={(c) => { onClose(); if (c.id !== vid) nav(switchTo(c.id)); }} />
    </Sheet>
  );
}
